import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Branding, CompanyProfile, TenantSettings } from '@goyone/shared';
import { api } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { date, money } from '../../lib/format';
import { Logo } from '../../components/Logo';
import { PrintSheet, useDocumentPaper } from '../../components/DocumentPaper';
import type { OfferRow } from '../hr/Offer';
import '../../styles/print.css';

/**
 * عرضُ العمل مطبوعًا — من عائلة مستندات الشركة نفسها.
 *
 * الترويسةُ هي ترويسةُ الفاتورة وكشف الحساب سواء: ما يصل يد من يُوظَّف
 * يبدو صادرًا من الجهة التي ستدفع له، لا ورقةً من شاشة.
 *
 * والنصُّ يُطبع كما حُرِّر لا كما يُصاغ الآن: العرضُ مستندٌ اتُّفق عليه،
 * وإعادةُ صياغته عند الطباعة تُخرج ورقةً غير التي قرأها.
 */

interface Profile {
  tenant: { name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

export function PrintOffer() {
  const { id = '' } = useParams();

  const offer = useQuery({
    queryKey: ['recruit', 'offer', id],
    queryFn: () => api<OfferRow>(`/hr/recruit/offers/${id}`),
  });

  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<Profile>('/settings'),
  });
  /* ورقةُ الشركة (ليتر هيد) إن رُفعت: تحلّ محلّ ترويسة النظام وتذييله. */
  const { paper, ready: paperReady } = useDocumentPaper('letterhead');

  const o = offer.data;
  const p = profile.data;

  if (offer.isError) {
    return <div className="print-root">تعذّر تحميل العرض.</div>;
  }
  if (!o || !p || !paperReady) {
    return <div className="print-root">جارٍ التحضير…</div>;
  }

  const { tenant, branding, company, settings } = p;
  const currency = settings.currencyLabel || o.currency;

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">
          اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.
        </span>
      </div>

      <PrintSheet paper={paper}>
        <header className="doc-header">
          <div className="doc-brand">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="doc-logo" />
            ) : (
              <Logo size={52} />
            )}
            <div>
              <div className="doc-company">
                {company.legalName || tenant.name}
              </div>
              {branding.documentHeader && (
                <div className="doc-tagline">{branding.documentHeader}</div>
              )}
              <div className="doc-company-meta">
                {company.address && <div>{company.address}</div>}
                <div className="doc-inline">
                  {company.phone && (
                    <span className="num">{company.phone}</span>
                  )}
                  {company.email && (
                    <span className="num">{company.email}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="doc-title-block">
            <div className="doc-kind">عرض عمل</div>
            <div className="doc-number num">{o.number}</div>
          </div>
        </header>

        <section className="doc-parties">
          <div className="doc-party">
            <div className="doc-label">إلى</div>
            <div className="doc-party-name">{o.candidateName}</div>
            <div>{o.title}</div>
            {o.department && <div>{o.department}</div>}
          </div>

          <div className="doc-dates">
            <div className="doc-date-row">
              <span className="doc-label">تاريخ المباشرة</span>
              <span className="num">{date(o.startDate)}</span>
            </div>
            {o.expiresOn && (
              <div className="doc-date-row">
                <span className="doc-label">مهلة الردّ</span>
                <span className="num">{date(o.expiresOn)}</span>
              </div>
            )}
            <div className="doc-date-row">
              <span className="doc-label">الراتب الأساسي</span>
              <span className="num">{money(o.baseSalary, currency)}</span>
            </div>
          </div>
        </section>

        {/* النصُّ كما حُرِّر — أسطرُه أسطرُه. */}
        <section className="doc-body rc-letter">{o.body}</section>

        {o.status === 'ACCEPTED' && o.acceptedName && (
          <section className="doc-terms">
            قُبل هذا العرض إلكترونيًّا باسم «{o.acceptedName}» في{' '}
            <span className="num">{date(o.decidedAt)}</span>.
          </section>
        )}

        <footer className="doc-footer">
          <div className="doc-footer-text">
            {branding.documentFooter ?? ""}
          </div>
          <div className="doc-signature">
            <div className="doc-sign-line" />
            <div className="doc-label">
              عن {company.legalName || tenant.name}
            </div>
          </div>
          <div className="doc-signature">
            <div className="doc-sign-line" />
            <div className="doc-label">توقيع المرشّح</div>
          </div>
        </footer>
      </PrintSheet>
    </div>
  );
}
